import { AccountMenu } from "@/components/account-menu";
import { ActiveSectionProvider } from "@/components/active-section";
import {
  ADMIN_VIEW_PREFIX,
  type ClientRole,
  ClientRoleProvider,
  RoleLink,
  type RolePath,
  useClientRole,
} from "@/components/client-role";
import { OPEN_COUNT } from "@/components/dashboard/attention";
import { CommandPalette } from "@/components/dashboard/command-palette";
import { EditPreviewProvider } from "@/components/edit-preview";
import { cn } from "@/lib/utils";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Activity, LayoutDashboard, type LucideIcon, TriangleAlert, Users } from "lucide-react";

// Detail surfaces that benefit from a wide canvas (the pipeline board). The home is also wide (it's a
// full roles table) — handled explicitly below since its path "/" would prefix-match everything.
const WIDE_PREFIXES = ["/search/"];

/** The current pathname with the admin-view prefix stripped, so the width rule can be written once
 *  against the viewer paths and work in both role trees. */
function useViewerPathname() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (pathname === ADMIN_VIEW_PREFIX) return "/";
  if (pathname.startsWith(`${ADMIN_VIEW_PREFIX}/`)) return pathname.slice(ADMIN_VIEW_PREFIX.length);
  return pathname;
}

/** Brand lockup — a co-brand WORDMARK, no monogram. This portal is a two-party engagement (the search firm
 *  × the client), which a single logo mark can't honestly represent and would only echo the words anyway —
 *  so the mark is dropped and the relationship IS the wordmark: "OW × Client". Set in the SAME sans as the
 *  tabs (not a separate display font) so the left side reads as one consistent voice; the "×" is muted so
 *  the two parties read as a pair. Links home — the wordmark is the global "back to overview" affordance. */
function Wordmark({ to }: { to?: RolePath }) {
  const inner = (
    <span className="whitespace-nowrap text-sm font-semibold tracking-tight text-foreground">
      OW <span className="font-normal text-muted-foreground">×</span> Client
    </span>
  );
  if (!to) return inner;
  return (
    <RoleLink
      to={to}
      activeOptions={{ exact: true }}
      aria-label="Overview"
      className="rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {inner}
    </RoleLink>
  );
}

/** ONE control system for the whole bar: every control is a 32px `rounded-lg` chip — transparent by default,
 *  a soft `bg-muted` hover, a focus ring. SELECTED is a SOLID inverted pill (`bg-foreground text-background`),
 *  NOT a raised surface. Reason, measured: the dark theme's surface ladder is so tight (card / muted /
 *  secondary all sit ~1.1:1 from the bar) that a `bg-card` "raised chip" is invisible in dark — 1.12:1, and
 *  its ring/shadow vanish too. The inverted pill clears ~18:1 in BOTH themes AND matches the app's filter
 *  chips, so "selected" reads one consistent way everywhere. `tabCls` is the worded variant; `iconCls` the
 *  glyph variant. */
const CTRL = "rounded-lg outline-none transition-all focus-visible:ring-2 focus-visible:ring-ring";
const CTRL_SELECTED = "bg-foreground text-background shadow-sm";
const CTRL_IDLE = "text-muted-foreground hover:bg-muted/60 hover:text-foreground";
const tabCls = (active: boolean) =>
  cn("inline-flex h-8 items-center gap-1.5 px-3 text-sm", CTRL, active ? `${CTRL_SELECTED} font-medium` : CTRL_IDLE);
const iconCls = (active: boolean) =>
  cn("inline-flex size-8 items-center justify-center", CTRL, active ? CTRL_SELECTED : CTRL_IDLE);

/** The Needs-attention count — a quiet neutral MONO pill (data, not decoration); `tabular-nums` so its width
 *  never jitters. The queue page carries the real severity colour, so the chrome stays calm. */
function NavCount({ value }: { value: number }) {
  return (
    <span className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-muted px-1 font-mono text-[11px] font-medium leading-none tabular-nums text-muted-foreground">
      {value}
    </span>
  );
}

/** How the primary nav renders its destinations — as WORDS or as GLYPHS. This was the open question, and the
 *  UX research settles it: icon-ONLY nav is a usability tax. Outside a few universal glyphs (search, home),
 *  abstract icons are interpreted correctly only ~34–60% of the time vs ~88% when labelled (NN/G; the classic
 *  MS-Outlook case — an icon-only toolbar went unused until labels were added). Our worst case is a client
 *  exec — an infrequent, non-technical viewer — guessing at a pulse glyph for "Activity". So destinations are
 *  WORDS; the only icons left in the bar are the two the research calls universal (the search magnifier and
 *  your avatar). `"icons"` exists purely to A/B the alternative — ship `"copy"`. */
const NAV_MODE: "copy" | "icons" = "copy";

type NavItem = {
  key: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  count?: number;
  roleTo?: RolePath; // role-relative destination (viewer "/…" ↔ admin "/admin-view/…"), via RoleLink
  to?: string; // absolute, admin-only destination, via a plain Link
};

/** The primary nav — ONE row, ONE treatment (no more worded tabs sitting beside icon-only tools). Every
 *  destination is the same kind of thing: Overview + Activity for everyone; Needs attention (the recruiter's
 *  triage queue, with its open count) and Team are OW-staff only. In `copy` mode each is a worded tab; in
 *  `icons` mode each is a glyph chip carrying its label in the tooltip/aria-label. */
function PrimaryNav() {
  const isAdmin = useClientRole() === "admin";
  const viewerPath = useViewerPathname();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const attentionTo = `${ADMIN_VIEW_PREFIX}/attention`;
  const teamTo = `${ADMIN_VIEW_PREFIX}/team`;

  const items = [
    { key: "overview", label: "Overview", icon: LayoutDashboard, active: viewerPath === "/", roleTo: "/" as RolePath },
    isAdmin && {
      key: "attention",
      label: "Needs attention",
      icon: TriangleAlert,
      active: pathname === attentionTo,
      to: attentionTo,
      count: OPEN_COUNT,
    },
    {
      key: "activity",
      label: "Activity",
      icon: Activity,
      active: viewerPath === "/activity",
      roleTo: "/activity" as RolePath,
    },
    isAdmin && { key: "team", label: "Team", icon: Users, active: pathname === teamTo, to: teamTo },
  ].filter(Boolean) as NavItem[];

  return (
    <nav aria-label="Primary" className="flex items-center gap-1">
      {items.map((it) => (
        <NavEntry key={it.key} item={it} />
      ))}
    </nav>
  );
}

/** One nav entry, rendered per NAV_MODE: a worded tab (`copy`) or a glyph chip whose label lives in the
 *  tooltip/aria-label (`icons`). Role-relative destinations go through RoleLink (so they track the current
 *  tree); the admin-only absolute paths use a plain Link. */
function NavEntry({ item }: { item: NavItem }) {
  const Icon = item.icon;
  const asIcons = NAV_MODE === "icons";
  const body = asIcons ? (
    <>
      <Icon className="size-[18px] shrink-0" aria-hidden />
      {item.count != null ? (
        <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-foreground px-1 font-mono text-[10px] font-medium leading-none tabular-nums text-background">
          {item.count}
        </span>
      ) : null}
    </>
  ) : (
    <>
      {item.label}
      {item.count != null ? <NavCount value={item.count} /> : null}
    </>
  );
  const props = {
    "aria-current": item.active ? ("page" as const) : undefined,
    className: cn(asIcons ? iconCls(item.active) : tabCls(item.active), asIcons && item.count != null && "relative"),
    ...(asIcons
      ? { "aria-label": item.count != null ? `${item.label} (${item.count})` : item.label, title: item.label }
      : {}),
  };
  return item.roleTo ? (
    <RoleLink to={item.roleTo} activeOptions={{ exact: true }} {...props}>
      {body}
    </RoleLink>
  ) : (
    <Link to={item.to as never} {...props}>
      {body}
    </Link>
  );
}

/** The top bar, two clean zones. LEFT = brand + navigation: the co-brand wordmark, a hairline divider (so the
 *  brand reads as its own thing, not a mismatched tab), then the primary nav — ONE worded row, every
 *  destination the same kind of thing. RIGHT (ml-auto) = utilities: search, then the account avatar, whose
 *  menu holds the switches that would otherwise litter the bar (view-as role, appearance/theme, notifications,
 *  sign out). So the only icons in the whole bar are the two research calls universal — the search magnifier
 *  and the avatar. A low (56px) sticky bar on a single hairline — no shadow.
 *
 *  There is NO Viewer/Admin switch in the bar: the two experiences are two ROUTES — viewer at "/", admin at
 *  "/admin-view" — and you hop between them from the account menu. (In production the role comes from auth;
 *  here it's the URL.) Same shell both roles; admin merely adds the Needs-attention tab and the Team tab. */
function TopBar() {
  return (
    <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-[1400px] items-center px-5 md:px-10">
        <div className="flex items-center gap-4">
          <Wordmark to="/" />
          <div aria-hidden className="h-5 w-px bg-border" />
          <PrimaryNav />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <CommandPalette />
          <AccountMenu />
        </div>
      </div>
    </header>
  );
}

/** Top-level client shell: a slim sticky top bar over a centered content column. Mounted once per role
 *  (audit C2) — the viewer tree at the root paths, the admin tree under `/admin-view` — with `role`
 *  provided to context so pages can reveal role-appropriate actions. The admin back-office keeps the
 *  shared sidebar `AppShell`; only the client portal moved to a top bar (2026-07 client review). */
export function Shell({ role }: { role: ClientRole }) {
  const pathname = useViewerPathname();
  const wide = pathname === "/" || WIDE_PREFIXES.some((p) => pathname.startsWith(p));
  return (
    <ClientRoleProvider role={role}>
      <EditPreviewProvider>
        <ActiveSectionProvider>
          <div className="min-h-screen">
            <TopBar />
            <main className={cn("mx-auto px-5 py-8 md:px-10 md:py-10", wide ? "max-w-[1400px]" : "max-w-[1120px]")}>
              <Outlet />
            </main>
          </div>
        </ActiveSectionProvider>
      </EditPreviewProvider>
    </ClientRoleProvider>
  );
}
